/**
 * Operations → Dropped.
 *
 * The terminal drop book — cases a Handling Firm took to Placed and then
 * dropped (terminated representation before settlement). Last in the
 * Operations lineup because drops are comparatively rare. Not a salvage
 * worklist: Dropped is terminal (resolved 2026-05-22). The surface exists for
 * the paperwork that follows a drop — LF must hold the firm's letter of
 * termination (the drop letter), and must notify the Originating Firm that the
 * case is shut down.
 *
 * LAYOUT (portal-imprint, D1=hybrid): a portal-style <Table>. Columns carry the
 * client, the two firms, the drop date, the drop-letter gate, and the reason;
 * rows open the shared CaseDetailPanel. Cases are sorted paperwork-first by the
 * composer (missing drop letter on top, then newest drop first).
 *
 * `DroppedCard` (below) is unchanged — The Surface renders dropped matters as
 * cards in its mixed-phase overlay.
 *
 * The OF-notified gate Chris also wants is a deliberate fast-follow write — it
 * has no field or automation today — so it is not on the surface yet.
 *
 * Spec: engineering/command-center/docs/port-notes/dropped.md (evicted 2026-08-29, #775 round 10)
 */

import { useState } from "react";
import { Check, Minus } from "lucide-react";
import type { DroppedCase } from "@command-center/shared";
import { BookHeading } from "@/components/book-heading";
import { SearchBar, matchesQuery } from "@/components/search-bar";
import { SurfaceTrigger } from "@/components/surface-trigger";
import { CaseDetailPanel } from "@/components/case-detail-panel";
import { Card } from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { droppedToDetail, type CaseDetail } from "@/lib/case-detail";
import { fmtDate } from "@/lib/format";
import { useDropped } from "@/lib/use-dropped";

export function DroppedPage() {
  const dropped = useDropped();
  const [query, setQuery] = useState("");
  // Lifted so a row stays open while you read it (portal parity: row → detail).
  const [detail, setDetail] = useState<CaseDetail | null>(null);

  if (dropped.isLoading) {
    return <div className="p-8 text-sm text-plane-muted">Loading…</div>;
  }

  if (dropped.isError) {
    return (
      <div className="p-8 text-sm text-error" data-testid="dropped-error">
        Couldn't load Dropped. It will retry automatically.
      </div>
    );
  }

  const cases = dropped.data?.cases ?? [];
  const searching = query.trim() !== "";
  const visible = cases.filter((c) => matchesQuery(c.client, query));

  return (
    <section className="mx-auto max-w-4xl p-6" data-testid="dropped">
      <BookHeading phaseKey="dropped">
        <span className="text-sm text-plane-muted" data-testid="dropped-count">
          {cases.length === 0
            ? ""
            : `${cases.length} dropped case${cases.length === 1 ? "" : "s"}`}
        </span>
      </BookHeading>

      <SearchBar value={query} onChange={setQuery} testId="dropped-search" />

      {visible.length === 0 ? (
        <div
          className="flex min-h-[50vh] items-center justify-center text-center"
          data-testid="dropped-empty"
        >
          <p className="text-lg text-plane-muted">
            {searching ? "No matches." : "No dropped cases."}
          </p>
        </div>
      ) : (
        <Card className="overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow className="cursor-default hover:bg-transparent">
                <TableHead>Client</TableHead>
                <TableHead>Referred by</TableHead>
                <TableHead>Handling firm</TableHead>
                <TableHead>Dropped</TableHead>
                <TableHead>Drop letter</TableHead>
                <TableHead>Reason</TableHead>
                <TableHead>
                  <span className="sr-only">Watch</span>
                </TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {visible.map((c) => (
                <DroppedRow
                  key={c.id}
                  droppedCase={c}
                  onOpenDetail={() => setDetail(droppedToDetail(c))}
                />
              ))}
            </TableBody>
          </Table>
        </Card>
      )}

      {detail ? (
        <CaseDetailPanel
          key={
            detail.actionTarget?.placementId ??
            detail.leadActionTarget?.leadId ??
            detail.name
          }
          detail={detail}
          onClose={() => setDetail(null)}
          testId="dropped-summary-panel"
        />
      ) : null}
    </section>
  );
}

/** One dropped case as a portal-style table row. The drop-letter gate is the
 *  work signal (a dash is the to-do); the reason truncates with the full text on
 *  hover. The row opens the detail panel. */
function DroppedRow({
  droppedCase: c,
  onOpenDetail,
}: {
  droppedCase: DroppedCase;
  onOpenDetail: () => void;
}) {
  return (
    <TableRow onClick={onOpenDetail} data-testid="dropped-card">
      <TableCell className="font-medium">{c.client}</TableCell>
      <TableCell
        className={c.originatingFirmName ? undefined : "text-muted-foreground"}
        data-testid="dropped-of-firm"
      >
        {c.originatingFirmName ?? "—"}
      </TableCell>
      <TableCell
        className={c.handlingFirmName ? undefined : "text-muted-foreground"}
        data-testid="dropped-hf-firm"
      >
        {c.handlingFirmName ?? "—"}
      </TableCell>
      <TableCell
        className="tabular-nums text-muted-foreground"
        data-testid="dropped-date"
      >
        {c.dropDate !== null ? fmtDate(c.dropDate, { year: true }) : "—"}
      </TableCell>
      {/* The paperwork gate — green check on file, muted dash missing (the work). */}
      <TableCell data-testid="dropped-letter" data-done={c.hasDropLetter}>
        {c.hasDropLetter ? (
          <Check className="h-4 w-4 text-success" />
        ) : (
          <Minus className="h-4 w-4 text-muted-foreground/40" />
        )}
      </TableCell>
      <TableCell className="max-w-[16rem] text-muted-foreground">
        {c.dropReason !== null ? (
          <span className="block truncate" data-testid="dropped-reason" title={c.dropReason}>
            {c.dropReason}
          </span>
        ) : null}
      </TableCell>
      <TableCell className="w-0 text-right" onClick={(e) => e.stopPropagation()}>
        <SurfaceTrigger leadId={c.leadRecordId} />
      </TableCell>
    </TableRow>
  );
}

/** One dropped case as a card — used by The Surface's mixed-phase overlay.
 *  who, the parties (OF to notify + the HF that dropped it), when, why, and
 *  whether the drop letter is on file. */
export function DroppedCard({
  droppedCase: c,
  onOpenDetail,
}: {
  droppedCase: DroppedCase;
  /** When provided (on The Surface), a "Case detail →" link opens the panel. */
  onOpenDetail?: () => void;
}) {
  return (
    <li className="rounded-lg border bg-card text-card-foreground px-4 py-3" data-testid="dropped-card">
      {/* Top line: who on the left, drop date on the right. */}
      <div className="flex items-start gap-3">
        <span className="min-w-0 flex-1 text-sm font-medium">{c.client}</span>
        <span className="shrink-0 text-right">
          <span
            className="text-sm font-semibold tabular-nums"
            data-testid="dropped-date"
          >
            {c.dropDate !== null ? fmtDate(c.dropDate, { year: true }) : "—"}
          </span>
          <span className="block text-[11px] text-muted-foreground">dropped</span>
        </span>
        <SurfaceTrigger leadId={c.leadRecordId} />
      </div>

      {/* The parties: originating firm (to notify) + handling firm (dropped it). */}
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
        <Party role="OF" name={c.originatingFirmName} testid="dropped-of-firm" />
        <Party role="HF" name={c.handlingFirmName} testid="dropped-hf-firm" />
      </div>

      {/* The paperwork gate — whether the firm's letter of termination is on
          file. A drop with no letter is the work. */}
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px]">
        <Gate label="Drop letter" done={c.hasDropLetter} testid="dropped-letter" />
      </div>

      {/* The drop reason — why representation was terminated, when recorded. */}
      {c.dropReason !== null ? (
        <p
          className="mt-2 text-xs leading-snug text-muted-foreground"
          data-testid="dropped-reason"
        >
          {c.dropReason}
        </p>
      ) : null}

      {onOpenDetail ? (
        <button
          type="button"
          onClick={onOpenDetail}
          className="mt-2 text-xs font-medium text-interactive hover:underline"
          data-testid="dropped-open-detail"
        >
          Case detail →
        </button>
      ) : null}
    </li>
  );
}

/**
 * One party — a role chip (OF / HF) and the firm name. The name is shown only
 * when present; an unknown link reads as just the chip.
 */
function Party({
  role,
  name,
  testid,
}: {
  role: "OF" | "HF";
  name: string | null;
  testid: string;
}) {
  return (
    <span className="inline-flex items-center gap-1.5" data-testid={testid}>
      <span className="rounded bg-muted px-1 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
        {role}
      </span>
      {name ? <span className="font-medium">{name}</span> : null}
    </span>
  );
}

/**
 * One paperwork gate — a labeled check. On file reads in foreground with a
 * green check; missing is muted with a dash (the dash is the work).
 */
function Gate({
  label,
  done,
  testid,
}: {
  label: string;
  done: boolean;
  testid: string;
}) {
  return (
    <span
      className="inline-flex items-center gap-1"
      data-testid={testid}
      data-done={done}
    >
      {done ? (
        <Check className="h-3 w-3 shrink-0 text-success" />
      ) : (
        <Minus className="h-3 w-3 shrink-0 text-muted-foreground/40" />
      )}
      <span className={done ? "text-foreground" : "text-muted-foreground"}>
        {label}
      </span>
    </span>
  );
}

